import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Switch, Table, Tag, message, Space } from "antd";
import { PlusOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useMsal } from "@azure/msal-react";
import { Header } from "../components/layout/header";
import * as TemplateService from "../services/TemplateService";

const SPECIALTY_LABELS = {
  behavioral_health: "Behavioral Health",
  weight_loss: "Weight Loss",
};

export default function TemplateLibrary({ options, selectedDoctorId, setSelectedDoctorId }) {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const logout = () => {
    instance.logout();
    sessionStorage.clear();
  };
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const loadTemplates = async (includeArchived) => {
    setLoading(true);
    try {
      const data = await TemplateService.listTemplates({
        page: 1,
        pageSize: 50,
        includeArchived,
      });
      setTemplates(data?.templates ?? []);
    } catch (e) {
      message.error(e?.response?.data?.detail || e?.message || "Failed to load templates.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates(showArchived);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onToggleArchived = (checked) => {
    setShowArchived(checked);
    loadTemplates(checked);
  };

  const onRestore = async (id) => {
    setRestoringId(id);
    try {
      await TemplateService.restoreTemplate(id);
      message.success("Template restored.");
      await loadTemplates(showArchived);
    } catch (e) {
      message.error(e?.response?.data?.detail || e?.message || "Failed to restore template.");
    } finally {
      setRestoringId(null);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name, row) => (
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={() => navigate(`/admin/template-builder/${row.id}`)}
        >
          {name}
        </Button>
      ),
    },
    {
      title: "Specialty",
      dataIndex: "specialty_tag",
      key: "specialty_tag",
      render: (tag) => (
        <Tag color={tag === "behavioral_health" ? "blue" : "green"}>
          {SPECIALTY_LABELS[tag] || tag}
        </Tag>
      ),
    },
    {
      title: "Updated",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (val) => (val ? new Date(val).toLocaleDateString() : "-"),
    },
    {
      title: "Status",
      key: "status",
      render: (_, row) =>
        row.archived_at ? (
          <Tag color="default">Archived</Tag>
        ) : (
          <Tag color="success">Active</Tag>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, row) =>
        row.archived_at ? (
          <Button
            size="small"
            loading={restoringId === row.id}
            onClick={() => onRestore(row.id)}
          >
            Restore
          </Button>
        ) : null,
    },
  ];

  const cardTitle = (
    <Space>
      <Button
        type="text"
        size="small"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/")}
        aria-label="Back to previous screen"
      >
        Back
      </Button>
      <span>Template Library</span>
    </Space>
  );

  return (
    <>
      <Header
        options={options}
        selectedDoctorId={selectedDoctorId}
        onSelectDoctor={setSelectedDoctorId}
        onLogout={logout}
      />
      <Card
        title={cardTitle}
        style={{ maxWidth: 900, margin: "24px auto" }}
        extra={
          <Space>
            <span style={{ fontSize: 13, color: "#595959" }}>Show archived</span>
            <Switch
              size="small"
              checked={showArchived}
              onChange={onToggleArchived}
              aria-label="Show archived templates"
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/admin/template-builder")}
            >
              New template
            </Button>
          </Space>
        }
      >
      <Table
        rowKey="id"
        loading={loading}
        dataSource={templates}
        columns={columns}
        pagination={false}
        locale={{ emptyText: "No templates yet. Create one to get started." }}
      />
    </Card>
    </>
  );
}
